import {useEffect,useState} from 'react';

type Project={id:string;parentId:string|null;title:string};
type Task={id:string;projectId:string;title:string;description:string;
  status:'OPEN'|'COMPLETED';submittedBy:string;createdAt:string};
type Data={projects:Project[];tasks:Task[]};
/** Native Tasks project tree. Owner-approved App Support is the first
 * project group wired to a persisted, permissioned task source. */
export function SupportTasksWorkspace({locationCode,focusTaskId}:{
  locationCode:string;focusTaskId?:string|null;
}){
  const [data,setData]=useState<Data|null>(null);
  const [status,setStatus]=useState('Loading Tasks…');
  const [chosen,setChosen]=useState<string|null>(null);
  const [busy,setBusy]=useState<string|null>(null);
  const reload=async(signal?:AbortSignal)=>{
    const r=await fetch('/api/v1/staff/support/tasks?'+
      new URLSearchParams({location:locationCode}),{
        credentials:'same-origin',cache:'no-store',signal});
    const body=await r.json() as {ok:boolean;data?:Data;error?:{code?:string}};
    if(!r.ok||!body.ok||!body.data)
      throw Error(body.error?.code==='ACCESS_DENIED'?
        'App Support permission is required to review these tasks.':
        'Tasks unavailable.');
    if(!signal?.aborted){setData(body.data);setStatus('');}
  };
  useEffect(()=>{
    const controller=new AbortController();
    setStatus('Loading Tasks…');setData(null);setChosen(null);
    void reload(controller.signal).catch(e=>{
      if(!controller.signal.aborted)setStatus(e instanceof Error?e.message:
        'Tasks unavailable.');
    });
    return ()=>controller.abort();
  },[locationCode]);
  const update=async(task:Task)=>{
    if(busy)return;
    setBusy(task.id);setStatus('');
    try{
      const r=await fetch('/api/v1/staff/support/tasks/'+
        encodeURIComponent(task.id),{
        method:'PATCH',credentials:'same-origin',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,completed:task.status!=='COMPLETED'}),
      });
      const body=await r.json() as {ok:boolean;error?:{code?:string}};
      if(!r.ok||!body.ok)throw Error(body.error?.code??'Task could not be saved.');
      await reload();
    }catch(e){setStatus(e instanceof Error?e.message:'Task could not be saved.');}
    finally{setBusy(null);}
  };
  const projects=data?.projects??[];
  const roots=projects.filter(project=>project.parentId===null);
  const selection=chosen??projects.find(p=>p.title==='East Coast Float Spa Support')?.id??null;
  const tasks=(data?.tasks??[]).filter(task=>selection===null||
    task.projectId===selection||
    projects.some(child=>child.parentId===selection&&task.projectId===child.id));
  return <section className="ecfs-support-tasks" aria-label="Tasks and Projects">
    <header><h2>Tasks</h2><button type="button"
      onClick={()=>void reload().catch(()=>setStatus('Tasks unavailable.'))}>
      Refresh</button></header>
    {status&&<p role={status==='Loading Tasks…'?'status':'alert'}>{status}</p>}
    <div className="ecfs-support-tasks-layout">
      <nav aria-label="Task projects" className="ecfs-support-projects">
        <h3>Projects</h3>
        {roots.map(root=><div key={root.id}>
          <button type="button" aria-current={selection===root.id?'page':undefined}
            onClick={()=>setChosen(root.id)}>{root.title}</button>
          {projects.filter(child=>child.parentId===root.id).map(child=>
            <button type="button" key={child.id}
              aria-current={selection===child.id?'page':undefined}
              className="ecfs-support-child-project"
              onClick={()=>setChosen(child.id)}>{child.title}</button>)}
        </div>)}
      </nav>
      <div className="ecfs-support-task-list">
        <h3>{projects.find(p=>p.id===selection)?.title??'Tasks'}</h3>
        {tasks.length===0&&data&&<p>No tasks in this project.</p>}
        {tasks.map(task=><article key={task.id}
          className={'ecfs-support-task'+(task.id===focusTaskId?' is-focused':'')}>
          <h4>{task.title}</h4>
          <p>{task.description}</p>
          <small>Submitted by {task.submittedBy} · {task.status}</small>
          <button type="button" disabled={busy!==null} onClick={()=>void update(task)}>
            {busy===task.id?'Saving…':
              task.status==='OPEN'?'Mark Completed':'Reopen'}
          </button>
        </article>)}
      </div>
    </div>
  </section>;
}
